// Cross-practice priority queue - the server's ranked list from
// GET /api/firm/dashboard. Severity, names, reasons and deadlines are the
// server's own; an unreachable server says so instead of inventing a
// ranking, and nothing here claims AI provenance.

import { useNavigate } from "react-router-dom";
import { useFirmDashboard } from "../../hooks/useFirmDashboard";
import type { PriorityQueueItem, QueueSeverity } from "../../api/firmDashboard";
import { daysUntilDay, itemDayKey } from "../../api/firmDeadlines";
import { Skeleton } from "../chrome/Skeleton";
import { SEP } from "../chrome/SepList";

const NUM_CLASS: Record<QueueSeverity, string> = {
  critical: "crit",
  urgent: "soon",
  ai_review: "review",
  normal: "",
};

const WHEN_CLASS: Record<QueueSeverity, string> = {
  critical: "crit",
  urgent: "soon",
  ai_review: "ok",
  normal: "ok",
};

function dueLabel(deadline: string): { text: string; date: string } {
  const days = daysUntilDay(deadline);
  const dayNoon = new Date(`${itemDayKey(deadline)}T12:00:00`);
  const text = days < 0 ? `${-days}d overdue` : days === 0 ? "due today" : `in ${days}d`;
  return {
    text,
    date: dayNoon.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
  };
}

function QueueRow({ item, index }: { item: PriorityQueueItem; index: number }) {
  const navigate = useNavigate();
  // The server falls back to a "(no-id)" literal when a case carries
  // neither eId nor caseNumber - such a row has nowhere to link.
  const hasId = !!item.eID && item.eID !== "(no-id)";
  const due = item.deadline ? dueLabel(item.deadline) : null;
  return (
    <div
      className={"cc-queue-item" + (index === 0 ? " highlighted" : "")}
      style={hasId ? { cursor: "pointer" } : undefined}
      onClick={hasId ? () => navigate(`/matter/${item.eID}`) : undefined}
    >
      <span className={`cc-queue-num ${NUM_CLASS[item.severity]}`.trim()}>{index + 1}</span>
      <div className="cc-queue-body">
        <div className="cc-queue-row1">
          <span className="name">{item.clientName || "(unnamed case)"}</span>
          {hasId && <span className="eid">{item.eID}</span>}
        </div>
        {item.reason && <div className="cc-queue-reason">{item.reason}</div>}
      </div>
      {due && (
        <div className="cc-queue-due">
          <span className={`when ${WHEN_CLASS[item.severity]}`}>{due.text}</span>
          <span className="date">{due.date}</span>
        </div>
      )}
    </div>
  );
}

export function PriorityQueue() {
  const q = useFirmDashboard();
  const items = q.data?.priorityQueue ?? [];
  return (
    <div className="cc-card">
      <div className="cc-card-head">
        <div className="cc-card-title">Today's priority queue</div>
        <div className="cc-card-sub">{SEP}urgency from open deadlines and conflicts</div>
      </div>
      {q.isLoading && (
        <div style={{ padding: "10px 18px" }}>
          <Skeleton width="100%" height={64} radius={8} />
        </div>
      )}
      {q.isError && (
        <div style={{ padding: "10px 18px", fontSize: 12, color: "var(--warn-600)" }}>
          Could not reach the server - the queue is unavailable.
        </div>
      )}
      {q.data && items.length === 0 && (
        <div style={{ padding: "10px 18px", fontSize: 12, color: "var(--muted)" }}>
          Nothing needs attention right now.
        </div>
      )}
      {items.length > 0 && (
        <div className="cc-queue">
          {items.map((item, idx) => (
            <QueueRow key={`${item.eID}-${idx}`} item={item} index={idx} />
          ))}
        </div>
      )}
    </div>
  );
}
